import axios, { AxiosRequestConfig, Method } from 'axios'
import { GET } from './RequestMethods'
import { redirectLogin, statusSet } from 'helpers/page'
import { checkRequireAfp, is_authed_async } from 'helpers/user'
import { get, includes, set } from 'lodash'
import store from 'utils/reduxStore'
import { doLogout } from '../actions'
import { general_error_toastr } from '../components/partials/Notification'
import { Auth } from 'aws-amplify'
import { randomString } from 'helpers/string'
import { logout } from '../actions/AuthActions'
import { environment } from '../services/environment'

let cancel_obj: Record<string, any> = {}

export const trigger_cancel = (key: string): Promise<string> => {
    const cancel_func = get(cancel_obj, key)

    if (!cancel_func) {
        return Promise.resolve('cancel_func.missing')
    }

    cancel_func('canceled')
    cancel_obj = set(cancel_obj, key, undefined)
    return Promise.resolve('canceled')
}

export const submitRequestWithPromise = async (
    method: Method,
    baseURL: string,
    url: string,
    params: Record<string, any> = {},
    options: { authUrl?: boolean, cancel_id?: string } = {},
    headers: Record<string, string> = {}
): Promise<any> => {
    let cancelToken = undefined

    const { authUrl = false, cancel_id } = options || {}

    if (authUrl) {
        url = await publicAuthedUrl(url)
    }

    if (cancel_id) {
        const tmp_cancel_token = axios.CancelToken.source()
        cancelToken = tmp_cancel_token.token
        set(cancel_obj, cancel_id, tmp_cancel_token.cancel)
    }

    const auth_token = await accessToken()

    if (method === GET) set(params, 'randStr', randomString())

    const config: AxiosRequestConfig = {
        url,
        baseURL,
        method,
        headers: {
            ...{
                'x-api-key': environment.apiKey,
                'content-type': 'application/json'
            },
            ...headers
        },
        cancelToken,
        [method === GET ? 'params' : 'data']: params
    }

    if (auth_token) {
        config.headers['Authorization'] = `Bearer ${auth_token}`
    }

    axios.interceptors.request.use(
        (config) => {
            statusSet('loading', true)
            return config
        },
        (error) => {
            statusSet('loading', false)
            return Promise.reject(error)
        }
    )

    axios.interceptors.response.use(
        async (response) => {
            statusSet('loading', false)
            return response
        },
        async (error) => {
            statusSet('loading', false)
            return handleErrorResponse(error)
        }
    )

    return axios
        .request(config)
        .then((response) => {
            checkRequireAfp(response.data)
            return response.data
        })
        .catch(async (error) => handleErrorResponse(error))
}

export const accessToken = async (): Promise<string | boolean> => {
    try {
        const res = await Auth.currentSession()
        const idToken = res.getIdToken()
        return idToken.getJwtToken()
    } catch (e) {
        return false
    }
}

export const handleErrorResponse = async (error: any): Promise<any> => {
    const { response } = error
    const { status = '0' } = response || {}
    const dispatch = store().dispatch

    if (axios.isCancel(error)) {
        return Promise.reject({ message: 'canceled', response: { status: 204 } })
    }

    switch (status) {
        case 401:
            await dispatch(logout())
            await dispatch(doLogout())
            if (includes(window.location.pathname, '/account')) {
                return redirectLogin()
            }
            break
        case 500:
            general_error_toastr()
            break
    }

    return Promise.reject(error)
}

export const publicAuthedUrl = async (public_url: string, authed_url: boolean | string = true): Promise<string> => {
    const authed = await is_authed_async()

    if (authed && authed_url === true) {
        return `authed/${public_url}`
    } else if (authed && typeof authed_url === 'string') {
        return authed_url
    }

    return public_url
}